import React, { useState } from 'react';
import styled from 'styled-components';
import { CartPackage, Image } from '../../types';
import { useCart } from '../../context/CartContext';
import { usePrices } from '../../context/PriceContext';
import ImageViewer from '../gallery/ImageViewer';

const PackageContainer = styled.div`
  background-color: ${props => props.theme.colors.card.background};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.medium};
  margin-bottom: ${props => props.theme.spacing.large};
  overflow: hidden;
  transition: transform ${props => props.theme.transitions.medium}, box-shadow ${props => props.theme.transitions.medium};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.large};
  }
`;

const PackageFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${props => props.theme.spacing.medium} ${props => props.theme.spacing.large};
  background-color: ${props => props.theme.colors.background.tertiary};
  border-top: 1px solid ${props => props.theme.colors.border.primary};
`;

const PackageTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin: 0;
  color: ${props => props.theme.colors.text.primary};
`;

const PackageSubtitle = styled.div`
  font-size: 0.85rem;
  color: ${props => props.theme.colors.text.secondary};
  margin-top: ${props => props.theme.spacing.xsmall};
`;

const PackagePrice = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${props => props.theme.colors.text.accent};
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: ${props => props.theme.spacing.medium};
  padding: ${props => props.theme.spacing.large};
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ImageThumbnail = styled.div`
  position: relative;
  border-radius: ${props => props.theme.borderRadius.small};
  overflow: hidden;
  transition: transform ${props => props.theme.transitions.fast};
  
  &:hover {
    transform: scale(1.03);
  }
`;

const StyledImage = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: ${props => props.theme.borderRadius.small};
`;

const MagnifyOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: ${props => props.theme.borderRadius.small};
  z-index: 5;
  
  ${ImageThumbnail}:hover & {
    opacity: 1;
  }
`;

const MagnifyIcon = styled.button`
  background-color: rgba(255, 255, 255, 0.85);
  border: none;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s, background-color 0.2s;
  
  &:hover {
    background-color: white;
    transform: scale(1.1);
  }
  
  svg {
    width: 24px;
    height: 24px;
    fill: #333;
  }
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.85);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity ${props => props.theme.transitions.medium}, 
    background-color ${props => props.theme.transitions.fast}, 
    transform ${props => props.theme.transitions.fast}, 
    border-color ${props => props.theme.transitions.fast};
  font-size: 17px;
  line-height: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  padding: 0;
  box-sizing: border-box;
  z-index: 10;
  
  ${ImageThumbnail}:hover & {
    opacity: 1;
  }
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.95);
    transform: scale(1.1);
    border-color: rgba(255, 255, 255, 0.7);
  }
`;

interface CartPackageItemProps {
  cartPackage: CartPackage;
}

const CartPackageItem: React.FC<CartPackageItemProps> = ({ cartPackage }) => {
  const { removeFromCart } = useCart();
  const { priceConfig, getPriceByType, getDiscountByType } = usePrices();
  
  // Estado para el ImageViewer
  const [selectedImage, setSelectedImage] = useState<Image | null>(null);
  
  // Función para abrir el ImageViewer
  const handleViewImage = (image: Image) => {
    setSelectedImage(image);
  };
  
  // Función para cerrar el ImageViewer
  const handleCloseViewer = () => {
    setSelectedImage(null);
  };
  
  const getPackageInfo = () => {
    switch (cartPackage.type) {
      case 'pack10':
        return {
          title: priceConfig.pack10?.nombre || 'Paquete de 10 imágenes',
          discount: getDiscountByType('pack10') ? `${getDiscountByType('pack10') * 100}%` : null,
          price: getPriceByType('pack10')
        };
      case 'pack5':
        return {
          title: priceConfig.pack5?.nombre || 'Paquete de 5 imágenes',
          discount: getDiscountByType('pack5') ? `${getDiscountByType('pack5') * 100}%` : null,
          price: getPriceByType('pack5')
        };
      case 'individual':
        return {
          title: priceConfig.individual?.nombre || 'Imagen individual',
          discount: null,
          price: getPriceByType('individual')
        };
      default:
        return {
          title: 'Paquete',
          discount: null,
          price: null
        };
    }
  };
  
  const packageInfo = getPackageInfo();
  const pricePerImage = packageInfo.price !== null ? `$${packageInfo.price} por imagen` : '';
  const discount = packageInfo.discount ? `${packageInfo.discount} descuento` : '';
  const subtitle = [pricePerImage, discount].filter(Boolean).join(' • ');
  
  return (
    <>
      <PackageContainer>
        <ImagesGrid>
          {cartPackage.images.map(image => (
            <ImageThumbnail key={image.id}>
              <StyledImage src={image.url} alt={image.title} />
              <MagnifyOverlay>
                <MagnifyIcon onClick={() => handleViewImage(image)} aria-label="Ver imagen completa">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                  </svg>
                </MagnifyIcon>
              </MagnifyOverlay>
              <RemoveButton onClick={() => removeFromCart(image.id)} aria-label="Eliminar">
                ×
              </RemoveButton>
            </ImageThumbnail>
          ))}
        </ImagesGrid>
        
        <PackageFooter>
          <div>
            <PackageTitle>{packageInfo.title}</PackageTitle>
            {subtitle && <PackageSubtitle>{subtitle}</PackageSubtitle>}
          </div>
          <PackagePrice>${cartPackage.price.toFixed(2)}</PackagePrice>
        </PackageFooter>
      </PackageContainer>
      
      {/* ImageViewer modal */}
      {selectedImage && (
        <ImageViewer 
          image={selectedImage} 
          onClose={handleCloseViewer}
          context="cart"
        />
      )}
    </>
  );
};

export default CartPackageItem;